import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import LeadDetailModal from '../Leads/LeadDetailModal'
import WalkInSignUpModal from './WalkInSignUpModal'
import { useHeaderSearch } from '../../contexts/HeaderSearchContext'
import {
  registrationsKeys,
  useCheckInRegistration,
  useUndoCheckIn
} from '../../hooks/useRegistrations'

const { FiUsers, FiChevronDown, FiChevronUp } = FiIcons

const ATTENDANCE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'checked-in', label: 'Checked In' },
  { id: 'not-yet', label: 'Not Yet' }
]

const SORT_COLUMNS = {
  name: 'name',
  created_at: 'created_at',
  invited_by: 'invited_by',
  attended: 'attended'
}

function getDisplayName(reg) {
  return (reg.student?.full_name ?? reg.lead?.full_name ?? reg.student_name) || ''
}

function getDisplayEmail(reg) {
  return (reg.student?.email ?? reg.lead?.email ?? reg.student_email) || ''
}

function getDisplayPhone(reg) {
  return (reg.student?.phone ?? reg.lead?.phone ?? reg.student_phone) || ''
}

function formatCheckInTime(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  })
}

function UndoCheckInConfirm({ studentName, onConfirm, onCancel, disabled }) {
  const popoverRef = useRef(null)

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  useEffect(() => {
    const onPointerDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onCancel()
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [onCancel])

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Confirm remove check-in"
      className="absolute left-0 top-6 z-30 w-56 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
    >
      <p className="text-sm text-gray-800">
        Remove check-in for <span className="font-medium">{studentName}</span>?
      </p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={disabled}
          className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
        >
          Confirm
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function SortableHeader({ label, column, sort, onSort }) {
  const active = sort.column === column
  return (
    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 hover:text-gray-800"
      >
        {label}
        {active && (
          <SafeIcon
            icon={sort.ascending ? FiChevronUp : FiChevronDown}
            className="h-3.5 w-3.5"
          />
        )}
      </button>
    </th>
  )
}

export default function EventRegistrationsTab({
  eventId,
  eventAgentId,
  registrations,
  registrationsLoading,
  registrationsError,
  authOptions,
  onExportClick
}) {
  const queryClient = useQueryClient()
  const { query: searchQuery, setScope, setQuery: setSearchQuery } = useHeaderSearch()

  const [attendanceFilter, setAttendanceFilter] = useState('all')
  const [sort, setSort] = useState({ column: 'created_at', ascending: false })
  const [detailLeadId, setDetailLeadId] = useState(null)
  const [walkInOpen, setWalkInOpen] = useState(false)
  const [checkInPendingId, setCheckInPendingId] = useState(null)
  const [undoConfirm, setUndoConfirm] = useState(null)

  const checkInMutation = useCheckInRegistration({ ...authOptions, eventId, eventIds: [eventId] })
  const undoCheckInMutation = useUndoCheckIn({ ...authOptions, eventId, eventIds: [eventId] })

  useEffect(() => {
    setScope('event-registrations')
    return () => {
      setScope(null)
      setSearchQuery('')
    }
  }, [setScope, setSearchQuery])

  const handleSort = useCallback((column) => {
    setSort((prev) => {
      if (prev.column === column) {
        return { column, ascending: !prev.ascending }
      }
      const defaultAsc = column === 'name' || column === 'invited_by'
      return { column, ascending: defaultAsc }
    })
  }, [])

  const filteredRegistrations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    let list = [...registrations]

    if (attendanceFilter === 'checked-in') {
      list = list.filter((r) => Boolean(r.attended))
    } else if (attendanceFilter === 'not-yet') {
      list = list.filter((r) => !r.attended)
    }

    if (q) {
      list = list.filter((reg) => {
        const name = getDisplayName(reg).toLowerCase()
        const email = getDisplayEmail(reg).toLowerCase()
        const phone = getDisplayPhone(reg).toLowerCase()
        return name.includes(q) || email.includes(q) || phone.includes(q)
      })
    }

    const col = sort.column
    const dir = sort.ascending ? 1 : -1

    list.sort((a, b) => {
      if (col === SORT_COLUMNS.name) {
        return getDisplayName(a).localeCompare(getDisplayName(b), undefined, { sensitivity: 'base' }) * dir
      }
      if (col === SORT_COLUMNS.invited_by) {
        const av = (a.invited_by || '').toLowerCase()
        const bv = (b.invited_by || '').toLowerCase()
        return av.localeCompare(bv) * dir
      }
      if (col === SORT_COLUMNS.attended) {
        const av = a.attended ? 1 : 0
        const bv = b.attended ? 1 : 0
        if (av !== bv) return (av - bv) * dir
        const at = a.attendance_verified_at ? new Date(a.attendance_verified_at).getTime() : 0
        const bt = b.attendance_verified_at ? new Date(b.attendance_verified_at).getTime() : 0
        return (at - bt) * dir
      }
      const at = a.created_at ? new Date(a.created_at).getTime() : 0
      const bt = b.created_at ? new Date(b.created_at).getTime() : 0
      return (at - bt) * dir
    })

    return list
  }, [registrations, searchQuery, attendanceFilter, sort])

  const checkedInCount = useMemo(
    () => registrations.filter((r) => Boolean(r.attended)).length,
    [registrations]
  )

  const performCheckIn = async (registrationId) => {
    if (checkInPendingId) return
    setUndoConfirm(null)
    setCheckInPendingId(registrationId)
    try {
      await checkInMutation.mutateAsync(registrationId)
    } finally {
      setCheckInPendingId(null)
    }
  }

  const requestUndoCheckIn = (reg) => {
    if (checkInPendingId) return
    const name = getDisplayName(reg) || 'this attendee'
    setUndoConfirm({ registrationId: reg.id, studentName: name })
  }

  const cancelUndoCheckIn = useCallback(() => {
    setUndoConfirm(null)
  }, [])

  const confirmUndoCheckIn = async () => {
    if (!undoConfirm?.registrationId || checkInPendingId) return
    const registrationId = undoConfirm.registrationId
    setCheckInPendingId(registrationId)
    try {
      await undoCheckInMutation.mutateAsync(registrationId)
      setUndoConfirm(null)
    } finally {
      setCheckInPendingId(null)
    }
  }

  const handleCheckboxChange = (reg) => {
    if (reg.attended) {
      requestUndoCheckIn(reg)
      return
    }
    void performCheckIn(reg.id)
  }

  const openLeadDetails = (leadId) => {
    if (!leadId) return
    setDetailLeadId(leadId)
  }

  const closeLeadDetails = () => setDetailLeadId(null)

  const handleWalkInRegistered = () => {
    queryClient.invalidateQueries({ queryKey: registrationsKeys.lists() })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">Event Registrations</h3>
          <p className="text-sm text-gray-500">
            {checkedInCount} checked in · {registrations.length} total
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setWalkInOpen(true)}
            className="rounded-lg border border-blue-600 px-4 py-2 text-blue-600 transition-colors hover:bg-blue-50"
          >
            Walk-in Sign Up
          </button>
          <button
            type="button"
            onClick={onExportClick}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Export List
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {ATTENDANCE_FILTERS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => setAttendanceFilter(chip.id)}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
              attendanceFilter === chip.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {registrationsError && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {registrationsError.message}
        </div>
      )}

      {registrationsLoading ? (
        <div className="py-8 text-center">
          <p className="text-gray-500">Loading registrations…</p>
        </div>
      ) : filteredRegistrations.length === 0 ? (
        <div className="py-8 text-center">
          <SafeIcon icon={FiUsers} className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <p className="text-gray-500">
            {registrations.length === 0
              ? 'No registrations yet'
              : 'No registrations match your search or filters'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Check-In" column="attended" sort={sort} onSort={handleSort} />
                <SortableHeader label="Name" column="name" sort={sort} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Phone
                </th>
                <SortableHeader
                  label="Date Registered"
                  column="created_at"
                  sort={sort}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Payment
                </th>
                <SortableHeader
                  label="Who Invited"
                  column="invited_by"
                  sort={sort}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredRegistrations.map((reg) => {
                const displayName = getDisplayName(reg) || '-'
                const displayEmail = getDisplayEmail(reg) || '-'
                const displayPhone = getDisplayPhone(reg) || '-'
                const hasLead = Boolean(reg.lead_id)
                const isToggling = checkInPendingId === reg.id
                const showUndoConfirm = undoConfirm?.registrationId === reg.id

                return (
                  <tr key={reg.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative inline-flex flex-col items-start gap-1">
                        <input
                          type="checkbox"
                          checked={Boolean(reg.attended)}
                          disabled={isToggling}
                          onChange={() => handleCheckboxChange(reg)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label={reg.attended ? 'Remove check-in' : 'Check in'}
                          aria-expanded={showUndoConfirm}
                        />
                        {showUndoConfirm && (
                          <UndoCheckInConfirm
                            studentName={undoConfirm.studentName}
                            onConfirm={() => void confirmUndoCheckIn()}
                            onCancel={cancelUndoCheckIn}
                            disabled={isToggling}
                          />
                        )}
                        {reg.attended && reg.attendance_verified_at && (
                          <span className="text-xs text-gray-500">
                            {formatCheckInTime(reg.attendance_verified_at)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {hasLead ? (
                        <button
                          type="button"
                          onClick={() => openLeadDetails(reg.lead_id)}
                          className="text-left text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {displayName}
                        </button>
                      ) : (
                        <span className="text-gray-900">{displayName}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{displayEmail}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{displayPhone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {reg.created_at ? new Date(reg.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          reg.status === 'converted'
                            ? 'bg-green-100 text-green-800'
                            : reg.status === 'contacted'
                              ? 'bg-yellow-100 text-yellow-800'
                              : reg.status === 'canceled'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {reg.status ?? 'registered'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          reg.payment_status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : reg.payment_status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {reg.payment_status ?? '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {reg.invited_by?.trim() ? reg.invited_by : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <LeadDetailModal
        leadId={detailLeadId}
        isOpen={Boolean(detailLeadId)}
        onClose={closeLeadDetails}
      />

      <WalkInSignUpModal
        isOpen={walkInOpen}
        onClose={() => setWalkInOpen(false)}
        eventId={eventId}
        eventAgentId={eventAgentId}
        authOptions={authOptions}
        onRegistered={handleWalkInRegistered}
      />
    </div>
  )
}
